import { Layout } from "@/components/layout/Layout";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Store, Factory, Hotel, Package } from "lucide-react";

const industries = [
  {
    id: "retail",
    icon: Store,
    title: "Retail & Distribution",
    description: "Supporting retailers and distributors with consistent product supply, competitive pricing, and reliable delivery schedules. From single-store operations to large distribution networks, we tailor solutions to your scale.",
    benefits: [
      "Flexible order quantities",
      "Private label options",
      "Seasonal product planning",
      "Just-in-time delivery support",
    ],
  },
  {
    id: "manufacturing",
    icon: Factory,
    title: "Manufacturing",
    description: "Supplying raw materials, components, and machinery to manufacturing enterprises. Our understanding of production requirements ensures you get exactly what your operations need.",
    benefits: [
      "Raw material sourcing",
      "Component supply chains",
      "Equipment procurement",
      "Technical specifications matching",
    ],
  },
  {
    id: "hospitality",
    icon: Hotel,
    title: "Hospitality",
    description: "Serving hotels, restaurants, and hospitality businesses with linens, furnishings, food products, and decorative items that meet the high standards of the hospitality industry.",
    benefits: [
      "Bulk linen supply",
      "Custom furnishing solutions",
      "Food & beverage sourcing",
      "Artisanal décor pieces",
    ],
  },
  {
    id: "fmcg",
    icon: Package,
    title: "FMCG",
    description: "Fast-moving consumer goods require fast-moving supply chains. We help FMCG companies source products that move quickly off shelves with consistent quality and competitive pricing.",
    benefits: [
      "High-volume procurement",
      "Quality consistency",
      "Rapid turnaround times",
      "Regulatory compliance support",
    ],
  },
];

const Industries = () => {
  return (
    <Layout>
      {/* Hero Section */}
      <section className="py-24 lg:py-32">
        <div className="container mx-auto px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-serif font-bold text-foreground mb-6">
              Industries We <span className="text-gradient-gold">Serve</span>
            </h1>
            <p className="text-lg md:text-xl text-muted-foreground leading-relaxed">
              Specialized expertise across diverse sectors, with tailored solutions 
              for each industry's unique requirements and challenges.
            </p>
          </div>
        </div>
      </section>

      {/* Industries Grid */}
      <section className="pb-24">
        <div className="container mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {industries.map((industry) => (
              <div
                key={industry.id}
                id={industry.id}
                className="p-8 lg:p-10 rounded-2xl bg-gradient-card border border-border hover:border-primary/50 transition-all duration-300"
              >
                <div className="w-16 h-16 rounded-xl bg-secondary flex items-center justify-center mb-6">
                  <industry.icon className="w-8 h-8 text-primary" />
                </div>
                
                <h2 className="text-2xl font-serif font-bold text-foreground mb-4">
                  {industry.title}
                </h2>
                
                <p className="text-muted-foreground leading-relaxed mb-6">
                  {industry.description}
                </p>

                {/* Benefits */}
                <ul className="space-y-2 mb-8">
                  {industry.benefits.map((benefit) => (
                    <li key={benefit} className="flex items-center gap-3">
                      <div className="w-1.5 h-1.5 rounded-full bg-primary" />
                      <span className="text-foreground/90 text-sm">{benefit}</span>
                    </li>
                  ))}
                </ul>

                <Link to="/contact">
                  <Button variant="goldOutline" size="default">
                    Discuss Your Needs
                  </Button>
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-card/50">
        <div className="container mx-auto px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-serif font-bold text-foreground mb-6">
              Your Industry Not Listed?
            </h2>
            <p className="text-lg text-muted-foreground mb-8">
              We serve businesses across all sectors. Contact us to discuss how we can 
              support your specific industry requirements.
            </p>
            <Link to="/contact">
              <Button variant="gold" size="xl">
                Get in Touch
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default Industries;
